import { useState, useEffect, useRef } from 'react';
import { adminAPI, getMediaFileUrl } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Upload, Trash2, Eye, EyeOff, Image, Download, Info } from 'lucide-react';

const MEDIA_SLUGS = [
  { value: 'logo', label: 'GB Sylt Logo', hint: 'Empfohlen: SVG oder PNG mit transparentem Hintergrund, max. 200 x 80 px' },
  { value: 'partner_logo', label: 'Partner Logo', hint: 'Empfohlen: SVG oder PNG mit transparentem Hintergrund, max. 200 x 80 px' },
  { value: 'hero_image', label: 'Hero Bild', hint: 'Empfohlen: 1920 x 1080 px (16:9), JPG/WebP' },
  { value: 'og_image', label: 'OG-Image (Social Media)', hint: 'Empfohlen: 1200 x 630 px (PNG/JPG)' },
  { value: 'favicon', label: 'Favicon', hint: 'Empfohlen: 512 x 512 px (PNG)' },
  { value: 'marketing_image', label: 'Marketing Bild', hint: 'Empfohlen: 1200 x 800 px (JPG/WebP)' },
  { value: 'qr_logo', label: 'QR-Code Logo', hint: 'Empfohlen: 1000 x 1000 px (PNG)' },
];

export default function MediaManager() {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedSlug, setSelectedSlug] = useState('logo');
  const [description, setDescription] = useState('');
  const [filterSlug, setFilterSlug] = useState('alle');
  const fileInputRef = useRef(null);

  const loadMedia = async () => {
    try {
      const res = await adminAPI.getMedia();
      setMedia(res.data);
    } catch {
      toast.error('Medien konnten nicht geladen werden');
    } finally { setLoading(false); }
  };

  useEffect(() => { loadMedia(); }, []);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('slug', selectedSlug);
      formData.append('description', description);
      await adminAPI.uploadMedia(formData);
      toast.success('Medium hochgeladen');
      setDescription('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      loadMedia();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload fehlgeschlagen');
    } finally { setUploading(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Medium wirklich loeschen?')) return;
    try {
      await adminAPI.deleteMedia(id);
      toast.success('Medium geloescht');
      loadMedia();
    } catch { toast.error('Loeschen fehlgeschlagen'); }
  };

  const handleToggle = async (id) => {
    try {
      await adminAPI.toggleMedia(id);
      toast.success('Status geaendert');
      loadMedia();
    } catch { toast.error('Aenderung fehlgeschlagen'); }
  };

  const handleDownload = (item) => {
    const url = getMediaFileUrl(item.id);
    const a = document.createElement('a');
    a.href = url;
    a.download = item.original_filename;
    a.target = '_blank';
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const selectedSlugInfo = MEDIA_SLUGS.find(s => s.value === selectedSlug);
  const filteredMedia = filterSlug === 'alle' ? media : media.filter(m => m.slug === filterSlug);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0E518D]" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="media-manager">
      <div>
        <h1 className="font-heading text-2xl font-bold text-slate-900">Medienverwaltung</h1>
        <p className="text-sm text-slate-500 font-body mt-1">Logos, Hero-Bilder und Marketing-Material verwalten</p>
      </div>

      <Card className="border-0 shadow-sm rounded-sm">
        <CardContent className="p-5 space-y-4">
          <h3 className="font-body font-semibold text-slate-800">Medium hochladen</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label className="font-body text-sm">Kategorie</Label>
              <select
                value={selectedSlug}
                onChange={(e) => setSelectedSlug(e.target.value)}
                className="mt-1 w-full h-10 rounded-sm border border-slate-200 bg-white px-3 text-sm font-body text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#0E518D] focus:border-transparent"
                data-testid="media-slug-select"
              >
                {MEDIA_SLUGS.map(s => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
              {selectedSlugInfo?.hint && (
                <div className="flex items-start gap-1.5 mt-1.5" data-testid="media-size-hint">
                  <Info className="w-3 h-3 text-[#0E518D] mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                  <p className="text-xs text-[#0E518D] font-body">{selectedSlugInfo.hint}</p>
                </div>
              )}
            </div>
            <div>
              <Label className="font-body text-sm">Beschreibung</Label>
              <Input
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="mt-1 rounded-sm font-body"
                placeholder="Optional"
                data-testid="media-description-input"
              />
            </div>
            <div>
              <Label className="font-body text-sm">Datei</Label>
              <div className="mt-1">
                <Input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleUpload}
                  className="rounded-sm font-body"
                  data-testid="media-file-input"
                />
              </div>
            </div>
          </div>
          {uploading && <p className="text-sm text-[#0E518D] font-body">Wird hochgeladen...</p>}
        </CardContent>
      </Card>

      {media.length > 0 && (
        <div className="flex items-center gap-3">
          <Label className="font-body text-sm text-slate-600">Filtern:</Label>
          <select
            value={filterSlug}
            onChange={(e) => setFilterSlug(e.target.value)}
            className="w-48 h-9 rounded-sm border border-slate-200 bg-white px-3 text-sm font-body text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#0E518D] focus:border-transparent"
            data-testid="media-filter-select"
          >
            <option value="alle">Alle Kategorien</option>
            {MEDIA_SLUGS.map(s => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
          <span className="text-xs text-slate-400 font-body">{filteredMedia.length} Medien</span>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredMedia.length === 0 ? (
          <div className="col-span-full text-center py-12">
            <Image className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-400 font-body">
              {media.length === 0 ? 'Noch keine Medien hochgeladen' : 'Keine Medien in dieser Kategorie'}
            </p>
          </div>
        ) : (
          filteredMedia.map(item => (
            <Card key={item.id} className={`border-0 shadow-sm rounded-sm overflow-hidden ${!item.active ? 'opacity-60' : ''}`} data-testid={`media-item-${item.id}`}>
              <div className="aspect-video bg-slate-100 flex items-center justify-center overflow-hidden">
                <img
                  src={getMediaFileUrl(item.id)}
                  alt={item.description || item.slug}
                  className="w-full h-full object-contain"
                  onError={(e) => { e.target.src = ''; e.target.alt = 'Bild nicht verfuegbar'; }}
                />
              </div>
              <CardContent className="p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <Badge variant="secondary" className="font-body text-xs">{item.slug}</Badge>
                  <Badge variant={item.active ? 'default' : 'outline'} className="font-body text-xs">
                    {item.active ? 'Aktiv' : 'Inaktiv'}
                  </Badge>
                </div>
                <p className="text-xs text-slate-500 font-body truncate">{item.original_filename}</p>
                <p className="text-xs text-slate-400 font-body">{(item.size / 1024).toFixed(1)} KB</p>
                <div className="flex flex-wrap gap-1.5 pt-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDownload(item)}
                    className="font-body text-xs text-[#0E518D] hover:text-[#0B4171] hover:bg-blue-50"
                    data-testid={`media-download-${item.id}`}
                  >
                    <Download className="w-3 h-3 mr-1" /> Herunterladen
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleToggle(item.id)}
                    className="font-body text-xs"
                    data-testid={`media-toggle-${item.id}`}
                  >
                    {item.active ? <EyeOff className="w-3 h-3 mr-1" /> : <Eye className="w-3 h-3 mr-1" />}
                    {item.active ? 'Deaktivieren' : 'Aktivieren'}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(item.id)}
                    className="font-body text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                    data-testid={`media-delete-${item.id}`}
                  >
                    <Trash2 className="w-3 h-3 mr-1" /> Loeschen
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}
